import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Upload, Video, Loader2, Download, Trash2, Sparkles, Scissors,
  Smartphone, Square, Crown, ChevronRight, Share2, Copy, Check,
} from "lucide-react";
import { getErrorMessage } from "../../utils/errorHandler";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 1024 * 1024 * 1024; // 1GB

function authHeader() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
}

/** Open a new tab to a social platform's web composer. The clip URL must be
 *  publicly reachable for the platforms that auto-embed; otherwise the user
 *  pastes manually after the page opens.
 */
const SOCIAL_TARGETS = [
  { id: "tiktok", label: "TikTok", url: "https://www.tiktok.com/upload" },
  { id: "youtube", label: "YT Shorts", url: "https://www.youtube.com/upload" },
  { id: "instagram", label: "Reels", url: "https://www.instagram.com/" },
  { id: "facebook", label: "Facebook", url: "https://www.facebook.com/" },
  { id: "x", label: "X", url: "https://twitter.com/compose/tweet" },
];

const STATUS_LABELS = {
  uploaded: "Uploaded",
  queued: "Queued",
  extracting_audio: "Extracting audio",
  transcribing: "Transcribing",
  analyzing: "Picking highlights",
  clipping: "Rendering clips",
  completed: "Completed",
  failed: "Failed",
};

const STATUS_COLOR = {
  completed: "bg-emerald-500/20 border-emerald-500/40 text-emerald-300",
  failed: "bg-red-500/20 border-red-500/40 text-red-300",
  default: "bg-amber-500/20 border-amber-500/40 text-amber-300",
};

export default function VideoClipperTab({ t }) {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Processing params
  const [durationPreset, setDurationPreset] = useState("30-60");
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  const [captionStyle, setCaptionStyle] = useState("standard");
  const [clipCount, setClipCount] = useState(5);

  const refreshList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/api/pulpit/video`, { headers: authHeader() });
      setJobs(res.data.jobs || []);
      if (selectedJob) {
        const fresh = (res.data.jobs || []).find((j) => j.job_id === selectedJob.job_id);
        if (fresh) setSelectedJob(fresh);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, t("load_failed") || "Load failed"));
    } finally {
      setLoadingList(false);
    }
  }, [selectedJob, t]);

  useEffect(() => { refreshList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll active job
  useEffect(() => {
    if (!selectedJob) return;
    const active = ["queued", "extracting_audio", "transcribing", "analyzing", "clipping"].includes(selectedJob.status);
    if (!active) return;
    const id = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/api/pulpit/video/${selectedJob.job_id}`, { headers: authHeader() });
        setSelectedJob(res.data);
        setJobs((js) => js.map((j) => (j.job_id === res.data.job_id ? res.data : j)));
      } catch { /* silent */ }
    }, 3500);
    return () => clearInterval(id);
  }, [selectedJob]);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(t("file_too_large_1gb") || "File exceeds 1GB limit");
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      const res = await axios.post(`${API}/api/pulpit/video/upload`, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
        timeout: 0,
      });
      toast.success(t("video_uploaded") || "Video uploaded");
      await refreshList();
      // Auto-select the new job
      const res2 = await axios.get(`${API}/api/pulpit/video/${res.data.job_id}`, { headers: authHeader() });
      setSelectedJob(res2.data);
    } catch (err) {
      toast.error(getErrorMessage(err, t("upload_failed") || "Upload failed"));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const startProcessing = async () => {
    if (!selectedJob) return;
    try {
      await axios.post(
        `${API}/api/pulpit/video/${selectedJob.job_id}/process`,
        {
          duration_preset: durationPreset,
          captions_enabled: captionsEnabled,
          caption_style: captionStyle,
          clip_count: clipCount,
        },
        { headers: authHeader() }
      );
      toast.success(t("processing_started") || "Processing started — this may take a few minutes");
      const res = await axios.get(`${API}/api/pulpit/video/${selectedJob.job_id}`, { headers: authHeader() });
      setSelectedJob(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, t("start_failed") || "Failed to start"));
    }
  };

  const deleteJob = async (job_id, e) => {
    e?.stopPropagation();
    if (!window.confirm(t("confirm_delete_video") || "Delete this video & all its clips?")) return;
    try {
      await axios.delete(`${API}/api/pulpit/video/${job_id}`, { headers: authHeader() });
      setJobs((js) => js.filter((j) => j.job_id !== job_id));
      if (selectedJob?.job_id === job_id) setSelectedJob(null);
      toast.success(t("deleted") || "Deleted");
    } catch (err) {
      toast.error(getErrorMessage(err, t("delete_failed") || "Delete failed"));
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const stopDefault = (e) => { e.preventDefault(); e.stopPropagation(); };

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-4">
      {/* LEFT: Job list */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-1">
              <Video className="w-3.5 h-3.5" />
              {t("my_sermon_videos") || "My Sermon Videos"}
            </CardTitle>
            <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px]">
              <Crown className="w-2.5 h-2.5 mr-1" />PRO+
            </Badge>
          </div>
          <CardDescription className="text-xs">
            {jobs.length} {t("videos") || "videos"}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-2 space-y-2">
          {/* Upload zone */}
          <div
            onDrop={onDrop}
            onDragOver={stopDefault}
            onDragEnter={stopDefault}
            className="border-2 border-dashed border-amber-500/30 rounded-lg p-4 text-center hover:border-amber-500/60 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            data-testid="video-upload-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*,audio/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="video-upload-input"
            />
            {uploading ? (
              <div className="space-y-2">
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-amber-400" />
                <Progress value={uploadProgress} className="h-1.5" />
                <p className="text-xs text-slate-400">{uploadProgress}%</p>
              </div>
            ) : (
              <>
                <Upload className="w-5 h-5 mx-auto text-amber-400 mb-1" />
                <p className="text-xs font-medium text-amber-200">
                  {t("upload_sermon_video") || "Upload Sermon Video"}
                </p>
                <p className="text-[10px] text-slate-500 mt-1">
                  {t("max_size_1gb") || "MP4, MOV, MP3 — max 1GB"}
                </p>
              </>
            )}
          </div>

          {/* Job list */}
          <ScrollArea className="h-[420px]">
            {loadingList && jobs.length === 0 ? (
              <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
            ) : jobs.length === 0 ? (
              <div className="text-xs text-slate-500 text-center py-6">
                {t("no_videos_yet") || "No videos yet — upload one above."}
              </div>
            ) : (
              <div className="space-y-1">
                {jobs.map((j) => (
                  <div
                    key={j.job_id}
                    onClick={() => setSelectedJob(j)}
                    className={`p-2 rounded cursor-pointer text-xs flex items-start justify-between gap-1 hover:bg-amber-500/10 ${selectedJob?.job_id === j.job_id ? "bg-amber-500/10 border border-amber-500/30" : ""}`}
                    data-testid={`video-job-card-${j.job_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate text-slate-200">{j.title}</div>
                      <div className="text-[10px] mt-0.5 flex items-center gap-1">
                        <Badge
                          variant="outline"
                          className={`text-[9px] px-1 py-0 border ${STATUS_COLOR[j.status] || STATUS_COLOR.default}`}
                        >
                          {STATUS_LABELS[j.status] || j.status}
                        </Badge>
                        {j.clips?.length > 0 && <span className="text-slate-500">{j.clips.length} clips</span>}
                      </div>
                    </div>
                    <button
                      onClick={(e) => deleteJob(j.job_id, e)}
                      className="opacity-50 hover:opacity-100 hover:text-red-400 transition-opacity"
                      data-testid={`delete-video-${j.job_id}`}
                      aria-label="Delete"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* RIGHT: Job detail */}
      <Card className="bg-slate-900/60 border-slate-800">
        {!selectedJob ? (
          <CardContent className="flex flex-col items-center justify-center py-20 text-center">
            <Scissors className="w-10 h-10 text-amber-500/40 mb-3" />
            <p className="text-sm text-slate-400 max-w-xs">
              {t("video_clipper_intro") ||
                "Upload a sermon video and AI will pick 5 highlight moments, transcribe them, and render shareable clips for Reels, Shorts & TikTok."}
            </p>
          </CardContent>
        ) : (
          <>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <CardTitle className="text-base truncate">{selectedJob.title}</CardTitle>
                  <CardDescription className="text-xs flex items-center gap-1 mt-1">
                    <Badge variant="outline" className={`text-[10px] px-1.5 ${STATUS_COLOR[selectedJob.status] || STATUS_COLOR.default}`}>
                      {STATUS_LABELS[selectedJob.status] || selectedJob.status}
                    </Badge>
                    {selectedJob.total_duration && (
                      <span className="text-slate-500">
                        · {Math.round(selectedJob.total_duration / 60)} min sermon
                      </span>
                    )}
                  </CardDescription>
                </div>
              </div>
              {selectedJob.status !== "completed" && selectedJob.status !== "uploaded" && selectedJob.status !== "failed" && (
                <div className="mt-2 space-y-1">
                  <Progress value={selectedJob.progress || 0} className="h-1.5" />
                  <p className="text-[10px] text-slate-400">{selectedJob.message || ""}</p>
                </div>
              )}
              {selectedJob.status === "failed" && (
                <p className="text-xs text-red-400 mt-1">{selectedJob.message}</p>
              )}
            </CardHeader>

            <CardContent>
              {/* Setup panel — visible until processing has produced clips */}
              {(selectedJob.status === "uploaded" || selectedJob.status === "failed") && (
                <div className="space-y-3 p-3 rounded-lg border border-amber-500/20 bg-amber-500/5 mb-4">
                  <h4 className="text-sm font-semibold text-amber-200 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" />
                    {t("clip_settings") || "Clip Settings"}
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-400">{t("clip_duration") || "Clip Duration"}</Label>
                      <Select value={durationPreset} onValueChange={setDurationPreset}>
                        <SelectTrigger data-testid="duration-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30-60">30–60 seconds (Reels/TikTok)</SelectItem>
                          <SelectItem value="60-90">60–90 seconds (YouTube Shorts)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-400">{t("number_of_clips") || "Number of Clips"}</Label>
                      <Input
                        type="number"
                        min={1}
                        max={10}
                        value={clipCount}
                        onChange={(e) => setClipCount(Math.max(1, Math.min(10, Number(e.target.value) || 5)))}
                        data-testid="clip-count-input"
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded bg-slate-950/40 border border-slate-800">
                      <div>
                        <Label className="text-xs">{t("burn_captions") || "Burn-in Captions"}</Label>
                        <p className="text-[10px] text-slate-500">{t("captions_help") || "Add subtitles directly into the video"}</p>
                      </div>
                      <Switch
                        checked={captionsEnabled}
                        onCheckedChange={setCaptionsEnabled}
                        data-testid="captions-toggle"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-400">{t("caption_style") || "Caption Style"}</Label>
                      <Select value={captionStyle} onValueChange={setCaptionStyle} disabled={!captionsEnabled}>
                        <SelectTrigger data-testid="caption-style-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="standard">Standard (white w/ outline)</SelectItem>
                          <SelectItem value="karaoke">Karaoke (word highlight)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button
                    onClick={startProcessing}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    data-testid="start-processing-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {selectedJob.status === "failed"
                      ? (t("retry_processing") || "Retry Processing")
                      : (t("generate_clips") || "Generate Clips")}
                  </Button>
                </div>
              )}

              {/* Clips grid */}
              {selectedJob.clips?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-amber-200 mb-2">
                    {t("generated_clips") || "Generated Clips"} ({selectedJob.clips.length})
                  </h4>
                  <ScrollArea className="h-[460px] pr-2">
                    <div className="grid sm:grid-cols-2 gap-3" data-testid="clips-grid">
                      {selectedJob.clips.map((c, i) => <ClipCard key={i} clip={c} t={t} />)}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Transcript */}
              {selectedJob.transcript_text && selectedJob.clips?.length > 0 && (
                <details className="mt-4 text-xs">
                  <summary className="cursor-pointer text-slate-400 hover:text-amber-300">
                    <ChevronRight className="w-3 h-3 inline" />
                    {t("view_transcript") || "View full transcript"}
                  </summary>
                  <p className="mt-2 p-2 rounded bg-slate-950/60 text-slate-300 max-h-60 overflow-y-auto whitespace-pre-wrap">
                    {selectedJob.transcript_text}
                  </p>
                </details>
              )}
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}

function ClipCard({ clip, t }) {
  const [aspect, setAspect] = useState("vertical");
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const url = clip.outputs?.[aspect];
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const buildUrl = (u) => {
    if (!u) return null;
    const full = `${API}${u}`;
    if (u.startsWith("/api/") && token) {
      return full + (u.includes("?") ? "&" : "?") + "token=" + encodeURIComponent(token);
    }
    return full;
  };
  const fullUrl = buildUrl(url);

  const captionText = [
    clip.title,
    clip.hook ? `"${clip.hook}"` : "",
    clip.caption,
  ].filter(Boolean).join("\n\n");

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(captionText);
      setCopied(true);
      toast.success(t("caption_copied") || "Caption copied!");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const shareTo = async (target) => {
    // Copy caption first (await so it lands before the new tab steals focus)
    await copyCaption();
    window.open(target.url, "_blank", "noopener,noreferrer");
    setShareOpen(false);
  };

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/60 overflow-hidden" data-testid={`clip-card-${clip.index}`}>
      {fullUrl && (
        <div className={`relative ${aspect === "vertical" ? "aspect-[9/16]" : "aspect-square"} bg-black`}>
          <video
            src={fullUrl}
            controls
            preload="metadata"
            className="w-full h-full object-contain"
            data-testid={`clip-video-${clip.index}`}
          />
        </div>
      )}
      <div className="p-2.5 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <h5 className="text-xs font-semibold text-amber-200 leading-tight">{clip.title}</h5>
          {clip.custom && <Badge variant="outline" className="text-[9px] border-purple-500/40 text-purple-300">Custom</Badge>}
        </div>
        {clip.hook && (
          <p className="text-[10px] italic text-slate-400 line-clamp-2">"{clip.hook}"</p>
        )}
        <p className="text-[10px] text-slate-500">
          {clip.duration?.toFixed(1)}s · {Math.floor(clip.start / 60)}:{String(Math.floor(clip.start % 60)).padStart(2, "0")} → {Math.floor(clip.end / 60)}:{String(Math.floor(clip.end % 60)).padStart(2, "0")}
        </p>
        {clip.caption && (
          <p className="text-[10px] text-slate-300 line-clamp-2">{clip.caption}</p>
        )}
        <div className="flex items-center gap-1 pt-1">
          <Button
            size="sm"
            variant={aspect === "vertical" ? "default" : "outline"}
            className={`h-6 px-2 text-[10px] ${aspect === "vertical" ? "bg-amber-600 hover:bg-amber-700" : ""}`}
            onClick={() => setAspect("vertical")}
            data-testid={`switch-vertical-${clip.index}`}
          >
            <Smartphone className="w-3 h-3 mr-0.5" />9:16
          </Button>
          <Button
            size="sm"
            variant={aspect === "square" ? "default" : "outline"}
            className={`h-6 px-2 text-[10px] ${aspect === "square" ? "bg-amber-600 hover:bg-amber-700" : ""}`}
            onClick={() => setAspect("square")}
            data-testid={`switch-square-${clip.index}`}
          >
            <Square className="w-3 h-3 mr-0.5" />1:1
          </Button>
          {fullUrl && (
            <Button
              size="sm"
              variant="outline"
              className="h-6 px-2 text-[10px] ml-auto"
              onClick={() => {
                const a = document.createElement("a");
                a.href = fullUrl;
                a.download = `${clip.title || "clip"}-${aspect}.mp4`;
                a.click();
              }}
              data-testid={`download-clip-${clip.index}-${aspect}`}
            >
              <Download className="w-3 h-3 mr-0.5" />
              {t("download") || "Download"}
            </Button>
          )}
        </div>

        {/* Share to Social row */}
        <div className="flex items-center gap-1 pt-1 border-t border-slate-800 mt-1">
          <Button
            size="sm" variant="ghost"
            className="h-6 px-2 text-[10px] text-emerald-300 hover:bg-emerald-500/10 flex-1"
            onClick={copyCaption}
            data-testid={`copy-caption-${clip.index}`}
          >
            {copied ? <Check className="w-3 h-3 mr-0.5" /> : <Copy className="w-3 h-3 mr-0.5" />}
            {copied ? (t("caption_copied") || "Copied!") : (t("copy_caption") || "Copy caption")}
          </Button>
          <Button
            size="sm" variant="ghost"
            className="h-6 px-2 text-[10px] text-pink-300 hover:bg-pink-500/10"
            onClick={() => setShareOpen((s) => !s)}
            data-testid={`share-toggle-${clip.index}`}
          >
            <Share2 className="w-3 h-3 mr-0.5" />
            {t("share") || "Share"}
          </Button>
        </div>
        {shareOpen && (
          <div className="grid grid-cols-5 gap-1 pt-1" data-testid={`share-options-${clip.index}`}>
            {SOCIAL_TARGETS.map((tgt) => (
              <Button
                key={tgt.id}
                size="sm" variant="outline"
                className="h-7 px-1 text-[9px] hover:bg-amber-500/10"
                onClick={() => shareTo(tgt)}
                data-testid={`share-${tgt.id}-${clip.index}`}
              >
                {tgt.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
